/**
 * @fileoverview Performance Monitoring System
 * Tracks cart operations and user interactions for performance insights
 * Based on Horizon's ThemePerformance implementation
 */

/**
 * Performance monitoring class for cart operations
 */
export class CartPerformance {
  /**
   * @param {string} metricPrefix - Prefix for all metrics
   */
  constructor(metricPrefix = 'cart-performance') {
    this.metricPrefix = metricPrefix;
    this.isEnabled = typeof performance !== 'undefined' && typeof performance.mark === 'function';
    this.metrics = new Map();
  }

  /**
   * Creates a starting performance marker
   * @param {string} benchmarkName - Name of the benchmark
   * @returns {PerformanceMark|null} The performance marker
   */
  createStartingMarker(benchmarkName) {
    if (!this.isEnabled) return null;

    const metricName = `${this.metricPrefix}:${benchmarkName}`;
    try {
      return performance.mark(`${metricName}:start`);
    } catch (error) {
      console.warn('Performance marking failed:', error);
      return null;
    }
  }

  /**
   * Measures performance from an event timestamp
   * @param {string} benchmarkName - Name of the benchmark
   * @param {Event} event - The event to measure from
   * @returns {PerformanceMeasure|null} The performance measure
   */
  measureFromEvent(benchmarkName, event) {
    if (!this.isEnabled || !event.timeStamp) return null;

    const metricName = `${this.metricPrefix}:${benchmarkName}`;
    
    try {
      const startMarker = performance.mark(`${metricName}:start`, {
        startTime: event.timeStamp
      });

      const endMarker = performance.mark(`${metricName}:end`);

      const measure = performance.measure(
        metricName,
        `${metricName}:start`,
        `${metricName}:end`
      );

      this.recordMetric(benchmarkName, measure.duration);
      return measure;
    } catch (error) {
      console.warn('Performance measurement failed:', error);
      return null;
    }
  }

  /**
   * Measures performance from a start marker
   * @param {PerformanceMark} startMarker - The start marker
   * @returns {PerformanceMeasure|null} The performance measure
   */
  measureFromMarker(startMarker) {
    if (!this.isEnabled || !startMarker) return null;

    const metricName = startMarker.name.replace(/:start$/, '');
    
    try {
      const endMarker = performance.mark(`${metricName}:end`);

      const measure = performance.measure(
        metricName,
        startMarker.name,
        endMarker.name
      );

      const benchmarkName = metricName.replace(`${this.metricPrefix}:`, '');
      this.recordMetric(benchmarkName, measure.duration);
      return measure;
    } catch (error) {
      console.warn('Performance measurement failed:', error);
      return null;
    }
  }

  /**
   * Records a metric for later analysis
   * @param {string} name - Metric name
   * @param {number} duration - Duration in milliseconds
   */
  recordMetric(name, duration) {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, {
        count: 0,
        totalTime: 0,
        averageTime: 0,
        minTime: Infinity,
        maxTime: 0,
        measurements: []
      });
    }

    const metric = this.metrics.get(name);
    metric.count++;
    metric.totalTime += duration;
    metric.averageTime = metric.totalTime / metric.count;
    metric.minTime = Math.min(metric.minTime, duration);
    metric.maxTime = Math.max(metric.maxTime, duration);
    
    // Keep last 10 measurements for analysis
    metric.measurements.push({
      duration,
      timestamp: Date.now()
    });
    
    if (metric.measurements.length > 10) {
      metric.measurements.shift();
    }
  }

  /**
   * Gets performance metrics summary
   * @returns {Object} Performance metrics
   */
  getMetrics() {
    const summary = {};
    
    for (const [name, metric] of this.metrics) {
      summary[name] = {
        count: metric.count,
        averageTime: Math.round(metric.averageTime * 100) / 100,
        minTime: Math.round(metric.minTime * 100) / 100,
        maxTime: Math.round(metric.maxTime * 100) / 100,
        totalTime: Math.round(metric.totalTime * 100) / 100
      };
    }
    
    return summary;
  }

  /**
   * Logs performance metrics to console
   */
  logMetrics() {
    if (!this.isEnabled) {
      console.log('Performance monitoring is disabled');
      return;
    }

    const metrics = this.getMetrics();
    console.group('🚀 Cart Performance Metrics');
    
    for (const [name, metric] of Object.entries(metrics)) {
      console.log(`${name}:`, {
        'Average Time': `${metric.averageTime}ms`,
        'Min Time': `${metric.minTime}ms`,
        'Max Time': `${metric.maxTime}ms`,
        'Total Calls': metric.count,
        'Total Time': `${metric.totalTime}ms`
      });
    }
    
    console.groupEnd();
  }

  /**
   * Clears all recorded metrics
   */
  clearMetrics() {
    this.metrics.clear();
  }
}

// Export singleton instance
export const cartPerformance = new CartPerformance();

// Add to window for debugging
if (typeof window !== 'undefined') {
  window.cartPerformance = cartPerformance;
} 